"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { fadeUp } from "@/lib/motion";

export default function CTA() {
  return (
    <section className="relative overflow-hidden bg-white py-28 sm:py-36">
      {/* Organic Topographic Contour Background Lines */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden opacity-25">
        <svg
          className="h-[46.875rem] w-[81.25rem] text-[#E86C26]"
          viewBox="0 0 1200 700"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M600 350 C550 300, 650 300, 600 350 Z"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C500 270, 700 270, 600 350 C520 420, 680 420, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C440 230, 760 230, 600 350 C460 460, 740 460, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C380 190, 820 190, 600 350 C400 500, 800 500, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C320 140, 880 140, 600 350 C340 550, 860 550, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C260 90, 940 90, 600 350 C280 600, 920 600, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C200 40, 1000 40, 600 350 C220 650, 980 650, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C140 -10, 1060 -10, 600 350 C160 700, 1040 700, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C80 -60, 1120 -60, 600 350 C100 750, 1100 750, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M600 350 C20 -110, 1180 -110, 600 350 C40 800, 1160 800, 600 350"
            stroke="currentColor"
            strokeWidth="1.2"
          />
        </svg>
      </div>

      {/* Floating Lock Icon — Top Right */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="absolute right-4 top-10 z-10 sm:right-12 sm:top-14 lg:right-20 lg:top-16"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-[#E86C260D] p-3 shadow-xs ring-1 ring-[#E86C2620] sm:h-24 sm:w-24"
        >
          <Image
            src="/lock.png"
            alt="Lock Icon"
            width={72}
            height={72}
            className="h-14 w-14 object-contain sm:h-16 sm:w-16"
            priority
          />
        </motion.div>
      </motion.div>

      {/* Floating Database Icon — Bottom Left */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="absolute left-4 bottom-10 z-10 sm:left-12 sm:bottom-14 lg:left-20 lg:bottom-16"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-[#E86C260D] p-3 shadow-xs ring-1 ring-[#E86C2620] sm:h-24 sm:w-24"
        >
          <Image
            src="/database.png"
            alt="Database Icon"
            width={72}
            height={72}
            className="h-14 w-14 object-contain sm:h-16 sm:w-16"
            priority
          />
        </motion.div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-3xl px-6 text-center lg:px-8">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="font-heading text-4xl font-semibold tracking-tight text-ink sm:text-5xl lg:text-6xl"
        >
          Ready to unlock your data?
        </motion.h2>
        <motion.p
          variants={fadeUp}
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto mt-6 max-w-2xl text-base text-ink-soft sm:text-lg"
        >
          Join 12,400+ developers and agencies using AgencyDB to power their
          data products.
        </motion.p>
        <motion.div
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <Link href="/marketplace">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="rounded-lg border border-black/15 bg-white px-6 py-3 text-sm font-medium text-ink shadow-xs transition-colors hover:bg-black/5"
            >
              Browse Marketplace
            </motion.button>
          </Link>
          <Link href="/signup">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              style={{
                background:
                  "linear-gradient(93.49deg, #E86C26 0.81%, #ED8951 49.4%, #BA561E 96.11%)",
              }}
              className="group flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-medium text-white shadow-lg shadow-brand/20 transition-opacity hover:opacity-90"
            >
              Start free Trial
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
